import { Company } from 'module/api/company/types';
import { UserAPI } from 'module/api/user';
import { LoginWidgetLinks } from 'module/authentication/types';
import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { Layout } from 'module/ui/components/atoms/layout';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { BodyShort01, ProductiveHeading04 } from 'module/ui/components/styles';
import { ArrowRight } from '@carbon/icons-react';
import { Link } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { useMutation } from '@tanstack/react-query';
import { Space } from 'antd';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ForgetPasswordSuccessPage } from './success';
import { CompanyLogo } from '../company-logo/logo';

const Container = styled(Layout.Content)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 88px 120px;
  align-items: flex-start;
`;
const Heading = styled.div`
  ${ProductiveHeading04};
  font-size: var(--heading-5-size);
`;
const UserInfo = styled.div`
  ${BodyShort01};
  color: var(--coolest-gray-70);
  margin: 0 0 var(--spacing-07) 0;
`;

const LinkText = styled.span`
  ${BodyShort01};
  margin-bottom: 0;
`;
const ErrorMessage = styled.p`
  color: var(--red-60);
`;
const RecoveryButton = styled(Button)`
  margin: var(--spacing-03) 0;
`;

interface Props {
  company: Company;
  onEmailSubmit: (email: string) => void;
  errorMessage?: string;
  actionLoading?: boolean;
  links: LoginWidgetLinks;
}
const ForgotPasswordForm: FC<Props> = ({
  company,
  onEmailSubmit,
  errorMessage,
  actionLoading,
  links,
}) => {
  const [form] = Form.useForm();
  const translatedPlaceholderText = useTranslation('forgot_password.email');
  const requiredText = useTranslation('forgot_password.email_required');
  const onSubmit = useCallback(async () => {
    try {
      const { email } = await form.validateFields();

      onEmailSubmit(email);
    } catch (error) {
      logger.log('password reset failed', { error });
    }
  }, [form, onEmailSubmit]);

  return (
    <Layout>
      <Container>
        <Space direction="vertical" size={16}>
          {<CompanyLogo data={company} />}
          <Heading>
            <FormattedMessage id="forgot_password.page_title" />
          </Heading>
          <Form
            form={form}
            layout="vertical"
            onSubmitCapture={onSubmit}
            onFinish={onSubmit}
          >
            <Form.Item
              name="email"
              label={translatedPlaceholderText}
              rules={[
                {
                  required: true,
                  message: requiredText,
                },
              ]}
            >
              <Input type="email" placeholder={translatedPlaceholderText} />
            </Form.Item>
            <ErrorMessage>
              {errorMessage != null ? errorMessage : ''}
            </ErrorMessage>
            <RecoveryButton
              loading={actionLoading}
              variant="primary"
              rightIcon={<ArrowRight size={16} />}
              onClick={onSubmit}
            >
              <FormattedMessage id="forgot_password.cta" />
            </RecoveryButton>
            <UserInfo>
              <FormattedMessage
                id="forgot_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={links.loginUrl}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          </Form>
        </Space>
      </Container>
    </Layout>
  );
};

interface ForgotPasswordProps {
  company: Company;
  isLoading: boolean;
  links: LoginWidgetLinks;
}

export const ForgotPassword: FC<ForgotPasswordProps> = ({
  company,
  isLoading,
  links,
}) => {
  const [emailValue, setEmailValue] = useState<string>('');
  const [isResetEmailSent, setIsResetEmailSent] = useState<boolean>(false);

  const triggerResetMail = async ({ email }) => {
    await UserAPI.triggerResetPasswordEmail(email)
      .then(() => {
        setEmailValue(email);
        setIsResetEmailSent(true);
      })
      .catch((err) => Promise.reject(err));
  };

  const {
    mutateAsync: triggerResetMailLink,
    isPending: resetMailSending,
    isError: isResetFailed,
  } = useMutation({
    mutationFn: triggerResetMail,
  });
  const onEmailSubmit = useCallback(
    (email: string) => {
      triggerResetMailLink({ email });
    },
    [triggerResetMailLink],
  );
  const renderScreen = () => {
    if (isLoading) {
      return <PageLoading />;
    }
    if (isResetEmailSent) {
      return <ForgetPasswordSuccessPage email={emailValue} links={links} />;
    }

    return (
      <ForgotPasswordForm
        company={company}
        links={links}
        onEmailSubmit={onEmailSubmit}
        actionLoading={resetMailSending}
        errorMessage={
          isResetFailed ? 'Something went wrong. Please try again.' : ''
        }
      />
    );
  };

  return renderScreen();
};
